// The Saju app's period charts as data components: 세운 (eleven years around the reference
// year), 월운 (the twelve solar-term months of that year) and the 100-year map (every year of
// the ten 대운). Each is /api/seun_list or /api/wolun_list of the local Saju app, drawn as
// editable cards and bars; a score the app does not give stays marked as not given.
import { SAJU_METRICS, SUBJECT_KEYS, componentError, requestSaju, sameSubject, validateCalculation, validateSubject } from './component-saju.mjs';

const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const exactKeys = (value, keys) => object(value) && Object.keys(value).length === keys.length && keys.every(key => Object.hasOwn(value, key));
const invalid = message => componentError('INVALID_COMPONENT_DATA', `${message} 기존 결과는 유지됩니다.`);
const text = (value, max) => typeof value === 'string' && value.length >= 1 && value.length <= max;
const STEMS = '갑을병정무기경신임계', BRANCHES = '자축인묘진사오미신유술해';
const ganji = value => text(value, 2) && value.length === 2 && STEMS.includes(value[0]) && BRANCHES.includes(value[1]);
const METRIC_KEYS = Object.keys(SAJU_METRICS);

function scoresOf(value, message) {
  if (!object(value)) throw invalid(message);
  return Object.fromEntries(METRIC_KEYS.map(metric => {
    const score = value[metric];
    if (score === undefined || score === null) return [metric, null];
    if (typeof score !== 'number' || !Number.isFinite(score) || score < 0 || score > 100) throw invalid('점수는 0~100 범위여야 합니다.');
    return [metric, score];
  }));
}

function periodProps(label, fields) {
  return props => {
    if (!object(props) || Object.keys(props).some(key => ![...SUBJECT_KEYS, ...fields].includes(key))) throw componentError('INVALID_COMPONENT', `${label} 설정 필드를 확인해 주세요.`);
    validateSubject(props);
    if (typeof props.title !== 'string' || !props.title.trim() || props.title.length > 100 || !/^#[0-9a-f]{6}$/i.test(props.accent)
      || (fields.includes('metric') && !Object.hasOwn(SAJU_METRICS, props.metric))) throw componentError('INVALID_COMPONENT', '제목, 운세 항목, 강조색을 확인해 주세요.');
    return props;
  };
}

/** The cards of seun and wolun: a label, the ganji, its ten gods and the chosen score as a bar. */
function periodCards(props, snapshot, { cards, subtitle, height }) {
  const elements = [];
  const rect = (key, x, y, width, h, fill, extra = {}) => elements.push({ key, type: 'rect', x, y, width, height: h, fill, ...extra });
  const write = (key, x, y, width, h, value, fontSize, color = '#24352f') => elements.push({ key, type: 'text', x, y, width, height: h, text: value, fontSize, fontFamily: 'Content KoPub Dotum', color });
  rect('background', 0, 0, 1000, height, '#f6f8f6');
  write('title', 32, 22, 936, 44, props.title, 30);
  write('subtitle', 32, 70, 936, 24, subtitle, 15, '#52665d');
  cards.forEach((card, slot) => {
    const x = 32 + (slot % 6) * 158, y = 110 + Math.floor(slot / 6) * 238;
    rect(`card-${slot}`, x, y, 146, 224, card.current ? '#fffaf0' : '#ffffff', { stroke: card.current ? props.accent : '#d8e0dc', strokeWidth: card.current ? 2 : 1 });
    write(`label-${slot}`, x + 12, y + 12, 124, 22, card.label, 13, '#52665d');
    write(`ganji-${slot}`, x + 12, y + 40, 124, 40, card.ganji, 30, props.accent);
    write(`gods-${slot}`, x + 12, y + 88, 124, 22, card.gods, 13, '#52665d');
    const score = card.scores[props.metric];
    rect(`track-${slot}`, x + 12, y + 124, 122, 10, '#e9eeeb');
    rect(`score-bar-${slot}`, x + 12, y + 124, Math.max(1, score === null ? 1 : 122 * score / 100), 10, props.accent, { dataProps: ['width'] });
    write(`score-${slot}`, x + 12, y + 146, 124, 30, score === null ? '점수 미제공' : `${Math.round(score * 10) / 10}점`, 22);
    write(`note-${slot}`, x + 12, y + 186, 124, 22, card.note, 12, '#52665d');
  });
  write('calculated-at', 32, height - 34, 936, 20, `계산: ${snapshot.calculatedAt.slice(0, 10)} · 사주 앱 계산 결과`, 12, '#52665d');
  return { width: 1000, height, elements };
}

// ── 세운 · 앞뒤 11년 ───────────────────────────────────────────────
const SEUN_SOURCE = 'sajug:/api/seun_list';
const SEUN_ITEM = ['year', 'age', 'ganji', 'stemTenGod', 'branchTenGod', 'unseong', 'scores'];

export function normalizeSeunResponse(payload) {
  if (!object(payload) || !Array.isArray(payload.items) || payload.items.length !== 11 || !Number.isInteger(payload.selected_year)) throw invalid('세운 11년을 모두 받지 못했습니다.');
  const items = payload.items.map((item, index) => {
    if (!object(item) || !Number.isInteger(item.year) || !Number.isInteger(item.age) || item.age < 0 || item.age > 200 || !ganji(item.ganji)
      || !text(item.ten_god_stem, 10) || !text(item.ten_god_branch, 10) || !text(item.unseong, 10)) throw invalid('세운 연도의 간지·십성 형식이 올바르지 않습니다.');
    if (index > 0 && item.year !== payload.items[index - 1].year + 1) throw invalid('세운 연도가 이어지지 않습니다.');
    return { year: item.year, age: item.age, ganji: item.ganji, stemTenGod: item.ten_god_stem, branchTenGod: item.ten_god_branch, unseong: item.unseong, scores: scoresOf(item.scores, '세운 점수 형식이 올바르지 않습니다.') };
  });
  if (!items.some(item => item.year === payload.selected_year)) throw invalid('기준 연도가 세운 범위에 없습니다.');
  return { selectedYear: payload.selected_year, items };
}

function validateSeunData(data) {
  if (!exactKeys(data, ['selectedYear', 'items']) || !Array.isArray(data.items) || data.items.some(item => !exactKeys(item, SEUN_ITEM) || !exactKeys(item.scores, METRIC_KEYS))) {
    throw componentError('INVALID_COMPONENT', '컴포넌트 결과의 필드가 누락되었거나 알 수 없는 필드가 있습니다.');
  }
  normalizeSeunResponse({ selected_year: data.selectedYear, items: data.items.map(item => ({ year: item.year, age: item.age, ganji: item.ganji,
    ten_god_stem: item.stemTenGod, ten_god_branch: item.branchTenGod, unseong: item.unseong, scores: item.scores })) });
}

const validateSeunProps = periodProps('세운', ['title', 'metric', 'accent']);
export async function calculateSeun(props, options = {}) {
  validateSeunProps(props);
  return { source: SEUN_SOURCE, calculatedAt: new Date().toISOString(), data: normalizeSeunResponse(await requestSaju('seun', props, options)) };
}
export function renderSeunScene(props, snapshot) {
  validateSeunProps(props);
  const { items, selectedYear } = snapshot.data;
  return periodCards(props, snapshot, { height: 620,
    subtitle: `${SAJU_METRICS[props.metric]} · ${items[0].year}–${items.at(-1).year}년 · 올해 ${selectedYear}년을 가운데 둔 11년`,
    cards: items.map(item => ({ label: `${item.year} · ${item.age}세${item.year === selectedYear ? ' · 올해' : ''}`, ganji: item.ganji,
      gods: `${item.stemTenGod} · ${item.branchTenGod}`, scores: item.scores, note: `운성 ${item.unseong}`, current: item.year === selectedYear })) });
}

// ── 월운 · 절기 12개월 ──────────────────────────────────────────────
const WOLUN_SOURCE = 'sajug:/api/wolun_list';
// `month` is the solar-term month (1 = 寅, from 입춘); the books name it by its solar month (寅 = 2월).
const WOLUN_MONTH = ['month', 'solarMonth', 'period', 'ganji', 'stemTenGod', 'branchTenGod', 'unseong', 'scores'];

/** The twelve months; `now` places the current month (the one whose solar term has begun and not ended). */
export function normalizeWolunResponse(payload, now = new Date()) {
  if (!object(payload) || !Array.isArray(payload.months) || payload.months.length !== 12) throw invalid('월운 12개월을 모두 받지 못했습니다.');
  let currentMonth = null;
  const months = payload.months.map((item, index) => {
    if (!object(item) || item.month !== index + 1 || item.solar_month !== (index + 1) % 12 + 1 || !text(item.period_label, 20) || !ganji(`${item.stem}${item.branch}`)
      || !text(item.ten_god_stem, 10) || !text(item.ten_god_branch, 10) || !text(item.unseong, 10)
      || !Number.isFinite(Date.parse(item.start_iso)) || !Number.isFinite(Date.parse(item.end_iso)) || Date.parse(item.end_iso) <= Date.parse(item.start_iso)) {
      throw invalid('월운 달의 절기·간지·십성 형식이 올바르지 않습니다.');
    }
    if (Date.parse(item.start_iso) <= now.getTime() && now.getTime() < Date.parse(item.end_iso)) currentMonth = item.month;
    return { month: item.month, solarMonth: item.solar_month, period: item.period_label, ganji: `${item.stem}${item.branch}`, stemTenGod: item.ten_god_stem, branchTenGod: item.ten_god_branch,
      unseong: item.unseong, scores: scoresOf(item.scores, '월운 점수 형식이 올바르지 않습니다.') };
  });
  return { currentMonth, months };
}

function validateWolunData(data) {
  if (!exactKeys(data, ['currentMonth', 'months']) || (data.currentMonth !== null && !(Number.isInteger(data.currentMonth) && data.currentMonth >= 1 && data.currentMonth <= 12))
    || !Array.isArray(data.months) || data.months.length !== 12 || data.months.some((item, index) => !exactKeys(item, WOLUN_MONTH) || item.month !== index + 1 || item.solarMonth !== (index + 1) % 12 + 1
      || !text(item.period, 20) || !ganji(item.ganji) || !text(item.stemTenGod, 10) || !text(item.branchTenGod, 10) || !text(item.unseong, 10) || !exactKeys(item.scores, METRIC_KEYS))) {
    throw componentError('INVALID_COMPONENT', '컴포넌트 결과의 필드가 누락되었거나 알 수 없는 필드가 있습니다.');
  }
  data.months.forEach(item => scoresOf(item.scores, '월운 점수 형식이 올바르지 않습니다.'));
}

const validateWolunProps = periodProps('월운', ['title', 'metric', 'accent']);
export async function calculateWolun(props, options = {}) {
  validateWolunProps(props);
  return { source: WOLUN_SOURCE, calculatedAt: new Date().toISOString(), data: normalizeWolunResponse(await requestSaju('wolun', props, options)) };
}
export function renderWolunScene(props, snapshot) {
  validateWolunProps(props);
  const { months, currentMonth } = snapshot.data;
  return periodCards(props, snapshot, { height: 620,
    subtitle: `${props.year}년 · ${SAJU_METRICS[props.metric]} · 입춘부터 절기로 나눈 12개월`,
    cards: months.map(item => ({ label: `${item.solarMonth}월 · ${item.period}${item.month === currentMonth ? ' · 이번 달' : ''}`, ganji: item.ganji,
      gods: `${item.stemTenGod} · ${item.branchTenGod}`, scores: item.scores, note: `운성 ${item.unseong}`, current: item.month === currentMonth })) });
}

// ── 100년 세운 지도 ─────────────────────────────────────────────────
const CENTURY_SOURCE = 'sajug:/api/seun_list?years=100';

/** Every year of the ten 대운, each 대운 a row of its ten years. */
export function normalizeCenturyResponse(payload) {
  if (!object(payload) || !Array.isArray(payload.items) || payload.items.length !== 100 || !Array.isArray(payload.daeun) || payload.daeun.length !== 10
    || !Number.isInteger(payload.selected_year)) throw invalid('100년 세운과 대운 10구간을 모두 받지 못했습니다.');
  const periods = payload.daeun.map((item, index) => {
    if (!object(item) || item.index !== index + 1 || !ganji(item.ganji) || !Number.isInteger(item.start_age) || item.end_age !== item.start_age + 9) throw invalid('대운 구간 형식이 올바르지 않습니다.');
    return { index: item.index, ganji: item.ganji, startAge: item.start_age, endAge: item.end_age };
  });
  const years = payload.items.map((item, index) => {
    if (!object(item) || !Number.isInteger(item.year) || !Number.isInteger(item.age) || !ganji(item.ganji)) throw invalid('세운 연도 형식이 올바르지 않습니다.');
    if (index > 0 && item.year !== payload.items[index - 1].year + 1) throw invalid('세운 연도가 이어지지 않습니다.');
    // Each 대운 row holds its ten years in order.
    if (item.age !== periods[Math.floor(index / 10)].startAge + index % 10) throw invalid('세운 연도가 대운 구간과 맞지 않습니다.');
    return { year: item.year, age: item.age, ganji: item.ganji, balance: scoresOf(item.scores, '세운 점수 형식이 올바르지 않습니다.').balance };
  });
  return { selectedYear: payload.selected_year, periods, years };
}

function validateCenturyData(data) {
  if (!exactKeys(data, ['selectedYear', 'periods', 'years']) || !Array.isArray(data.periods) || !Array.isArray(data.years)
    || data.periods.some(item => !exactKeys(item, ['index', 'ganji', 'startAge', 'endAge'])) || data.years.some(item => !exactKeys(item, ['year', 'age', 'ganji', 'balance']))) {
    throw componentError('INVALID_COMPONENT', '컴포넌트 결과의 필드가 누락되었거나 알 수 없는 필드가 있습니다.');
  }
  normalizeCenturyResponse({ selected_year: data.selectedYear,
    daeun: data.periods.map(item => ({ index: item.index, ganji: item.ganji, start_age: item.startAge, end_age: item.endAge })),
    items: data.years.map(item => ({ year: item.year, age: item.age, ganji: item.ganji, scores: { balance: item.balance } })) });
}

const validateCenturyProps = periodProps('100년 세운', ['title', 'accent']);
export async function calculateCentury(props, options = {}) {
  validateCenturyProps(props);
  return { source: CENTURY_SOURCE, calculatedAt: new Date().toISOString(), data: normalizeCenturyResponse(await requestSaju('century', props, options)) };
}
export function renderCenturyScene(props, snapshot) {
  validateCenturyProps(props);
  const { periods, years, selectedYear } = snapshot.data, elements = [];
  const rect = (key, x, y, width, h, fill, extra = {}) => elements.push({ key, type: 'rect', x, y, width, height: h, fill, ...extra });
  const write = (key, x, y, width, h, value, fontSize, color = '#24352f') => elements.push({ key, type: 'text', x, y, width, height: h, text: value, fontSize, fontFamily: 'Content KoPub Dotum', color });
  rect('background', 0, 0, 1000, 820, '#f6f8f6');
  write('title', 32, 22, 936, 44, props.title, 30);
  write('subtitle', 32, 70, 936, 24, `대운 10구간 × 10년 · 종합운 · ${years[0].year}–${years.at(-1).year}년 · 올해 ${selectedYear}년`, 15, '#52665d');
  periods.forEach((period, row) => {
    const y = 108 + row * 68;
    rect(`period-box-${row}`, 32, y, 160, 60, '#eef2ef');
    write(`period-${row}`, 44, y + 8, 140, 44, `${period.index}대운 ${period.ganji}\n${period.startAge}~${period.endAge}세`, 14, '#24352f');
    for (let column = 0; column < 10; column++) {
      const slot = row * 10 + column, item = years[slot], x = 200 + column * 77, current = item.year === selectedYear;
      rect(`cell-${slot}`, x, y, 72, 60, current ? '#fffaf0' : '#ffffff', { stroke: current ? props.accent : '#d8e0dc', strokeWidth: current ? 2 : 1 });
      const bar = item.balance === null ? 1 : Math.max(1, 30 * item.balance / 100);
      rect(`bar-${slot}`, x + 8, y + 52 - bar, 56, bar, props.accent, { dataProps: ['y', 'height'] });
      write(`year-${slot}`, x + 6, y + 4, 64, 20, `${item.year} ${item.balance === null ? '—' : Math.round(item.balance)}`, 11, current ? props.accent : '#52665d');
    }
  });
  write('calculated-at', 32, 790, 936, 20, `계산: ${snapshot.calculatedAt.slice(0, 10)} · 사주 앱 세운 계산 결과 · 막대는 종합운 점수`, 12, '#52665d');
  return { width: 1000, height: 820, elements };
}

const component = definition => Object.freeze({ version: 1, calculationChanged: (before, after) => !sameSubject(before, after), ...definition });
const field = (name, label, kind, fallback, extra = {}) => Object.freeze({ name, label, kind, default: fallback, ...extra });

export const SEUN_COMPONENT = component({ type: 'saju.seun', label: '세운 · 앞뒤 11년',
  description: '생년월일로 계산한 올해 앞뒤 11년의 세운 간지·십성·점수를 현재 페이지에 추가합니다.',
  fields: Object.freeze([field('title', '제목', 'text', '세운 · 앞뒤 11년'), field('metric', '표시할 운세', 'select', 'balance', { options: SAJU_METRICS }), field('accent', '강조색', 'color', '#1565c0')]),
  validateProps: validateSeunProps, validateSnapshot: snapshot => validateCalculation(snapshot, SEUN_SOURCE, validateSeunData),
  calculate: calculateSeun, render: renderSeunScene });
export const WOLUN_COMPONENT = component({ type: 'saju.wolun', label: '월운 · 절기 12개월',
  description: '생년월일로 계산한 기준 연도의 절기 12개월 월운을 현재 페이지에 추가합니다.',
  fields: Object.freeze([field('title', '제목', 'text', '월운 · 절기 12개월'), field('metric', '표시할 운세', 'select', 'balance', { options: SAJU_METRICS }), field('accent', '강조색', 'color', '#6a1b9a')]),
  validateProps: validateWolunProps, validateSnapshot: snapshot => validateCalculation(snapshot, WOLUN_SOURCE, validateWolunData),
  calculate: calculateWolun, render: renderWolunScene });
export const CENTURY_COMPONENT = component({ type: 'saju.seun-century', label: '100년 세운 지도',
  description: '생년월일로 계산한 대운 10구간의 모든 해(100년) 세운 종합운을 현재 페이지에 추가합니다.',
  fields: Object.freeze([field('title', '제목', 'text', '100년 세운 지도'), field('accent', '강조색', 'color', '#2e7d32')]),
  validateProps: validateCenturyProps, validateSnapshot: snapshot => validateCalculation(snapshot, CENTURY_SOURCE, validateCenturyData),
  calculate: calculateCentury, render: renderCenturyScene });
